export interface FilterInterface {
  selected: string | string[];
  multiple: boolean;
  list: string[];
  onFilterSelected(filter: string): void;
  isFilterSelected(filter: string): boolean;
  isFilterFirst(filter: string): boolean;
  isFilterLast(filter: string): boolean;
  updateFilterAria(): void;
}

const FILTER_ALL = 'all';

const Filter = ({
  initial,
  multiple = false,
  list = [],
}: {
  initial?: string;
  multiple?: boolean;
  list?: string[];
}): FilterInterface => ({
  selected: initial ? initial : multiple ? [] : '',
  multiple: multiple,
  list: list,

  /**
   * Handle the current selected filter.
   * Updates state then syncs aria-checked (radio/checkbox) and aria-current (label).
   * When not multiple, removes aria-checked and aria-current from other options.
   *
   * @param filter - The current filter
   */
  onFilterSelected(filter: string): void {
    if (this.multiple) {
      // Handle multi filters
      if (!this.selected.includes(filter)) {
        (<string[]>this.selected).push(filter);
      }
    } else {
      // Handle single filter
      if (this.selected !== filter) {
        this.selected = filter;
      }
    }
    this.updateFilterAria();
  },

  /**
   * Updates aria-checked on inputs and aria-current on labels from current selection.
   * Single select: only the selected option keeps ARIA; others are cleared.
   */
  updateFilterAria(): void {
    const el = (this as unknown as { $el?: HTMLElement }).$el;
    if (!el) return;

    const ul = el.querySelector('ul');
    if (!ul) return;

    ul.querySelectorAll<HTMLInputElement>('input[type="radio"], input[type="checkbox"]').forEach((input) => {
      if (this.selected.includes(input.value)) {
        input.setAttribute('aria-checked', 'true');
      } else {
        input.removeAttribute('aria-checked');
      }
    });

    ul.querySelectorAll('label').forEach((label) => {
      const input = label.querySelector<HTMLInputElement>('input');
      const selected = input ? this.selected.includes(input.value) : false;
      if (selected) {
        label.setAttribute('aria-current', 'true');
      } else {
        label.removeAttribute('aria-current');
      }
    });
  },

  /**
   * Indicates whether or not if the given filter has been selected yet.
   *
   * @param filter - The filter
   *
   * @returns boolean
   */
  isFilterSelected(filter: string): boolean {
    return this.multiple
      ? this.selected.includes(filter)
      : this.selected === filter || this.selected === FILTER_ALL;
  },

  /**
   * Indicates whether or not the given filter is the first one.
   *
   * @param filter - The filter
   *
   * @returns boolean
   */
  isFilterFirst(filter: string): boolean {
    if (this.multiple) {
      return (<string[]>this.list).indexOf(filter) === 0;
    } else {
      return this.selected === filter;
    }
  },

  /**
   * Indicates whether or not the given filter is the last one.
   *
   * @param filter - The filter
   *
   * @returns boolean
   */
  isFilterLast(filter: string): boolean {
    if (this.multiple) {
      return (
        (<string[]>this.list).indexOf(filter) ===
        (<string[]>this.list).length - 1
      );
    } else {
      return this.selected === filter;
    }
  },
});

export default Filter;
